import React, { useContext, useEffect, useMemo, useState } from "react";
import useMeasure from "react-use-measure";
import styled from "styled-components";
import {
  VictoryChart,
  VictoryLine,
  VictoryAxis,
  VictoryLegend,
  VictoryTooltip,
  createContainer,
} from "victory";
import { UserContext } from "../../../contexts/UserContext";

const VictoryZoomVoronoiContainer = createContainer("zoom", "voronoi");

const StyledChart = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  padding: 1rem;
`;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const hexToRgb = (hex) => {
  const clean = hex.replace("#", "");

  return {
    r: parseInt(clean.substring(0, 2), 16),
    g: parseInt(clean.substring(2, 4), 16),
    b: parseInt(clean.substring(4, 6), 16),
  };
};

const rgbToHex = ({ r, g, b }) =>
  `#${[r, g, b].map((value) => value.toString(16).padStart(2, "0")).join("")}`;

const interpolateColor = (fromHex, toHex, ratio) => {
  const from = hexToRgb(fromHex);
  const to = hexToRgb(toHex);

  const t = clamp(ratio, 0, 1);

  return rgbToHex({
    r: Math.round(from.r + (to.r - from.r) * t),
    g: Math.round(from.g + (to.g - from.g) * t),
    b: Math.round(from.b + (to.b - from.b) * t),
  });
};

function WeeklySpectrum({ dataset, valueMode }) {
  const [ref, bounds] = useMeasure();
  const [measuredWidth, setMeasuredWidth] = useState(null);
  const isPercentMode = valueMode === "pct";

  const chartWidth = measuredWidth ?? 800;
  const chartHeight = chartWidth / 2.2;

  const { userDetails } = useContext(UserContext);
  const allowZoom = userDetails?.userId != null;

  
    const spectrumCurrentColor = "#effff1"; // light, recent week
    const spectrumOldColor = "#244e38"; // dark, oldest week

  useEffect(() => {
    if (bounds.width > 0 && measuredWidth === null) {
      setMeasuredWidth(bounds.width);
    }
  }, [bounds.width, measuredWidth]);

const { weeks, yMax } = useMemo(() => {
  if (!dataset?.weeks?.length) {
    return {
      weeks: [],
      yMax: 500,
    };
  }

  console.log("Spectrum data", dataset)

  const maxWeekSequence = Math.max(
    ...dataset.weeks.map((week) => week.weekSequence ?? 0)
  );

  const sortedWeeks = [...dataset.weeks]
    // Draw oldest first, current week last/on top
    .sort((a, b) => b.weekSequence - a.weekSequence)
    .map((week) => {
      const ageRatio =
        maxWeekSequence === 0 ? 0 : (week.weekSequence ?? 0) / maxWeekSequence;

      const color = interpolateColor(
        spectrumCurrentColor,
        spectrumOldColor,
        ageRatio
      );

      return {
        weekSequence: week.weekSequence,
        label: week.label,
        color,
        opacity: 0.95,
        strokeWidth: week.weekSequence === 0 ? 3 : 1.7,

        data:
          week.points?.map((p) => {
            const occupied = p.locationOccupiedTotal ?? 0;
            const capacity = p.locationCountTotal ?? 1;
            const percent = (occupied / capacity) * 100;

            return {
              x: p.minuteOfWeek,
              y: isPercentMode ? percent : occupied,

              rawValue: occupied,
              percentValue: percent,
              capacityValue: capacity,

              weekLabel: week.label,
              weekSequence: week.weekSequence,
              serverTime: p.serverTime,
            };
          }) ?? [],
      };
    });

  const maxValue = Math.max(
    500,
    ...sortedWeeks.flatMap((week) => week.data.map((point) => point.y))
  );

  const roundedMax = Math.ceil(maxValue / 100) * 100;

  return {
    weeks: sortedWeeks,
    yMax: roundedMax,
  };
}, [dataset, isPercentMode]);


const yTicks = useMemo(() => {
  if (isPercentMode) return [0, 20, 40, 60, 80, 100];

  const ticks = [];
  for (let value = 0; value <= yMax; value += 100) {
    ticks.push(value);
  }
  return ticks;
}, [yMax, isPercentMode]);


  const xTickValues = [0, 1440, 2880, 4320, 5760, 7200, 8640];

  const formatMinuteOfWeek = (minute) => {
    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const dayIndex = Math.floor(minute / 1440);
    return days[dayIndex] ?? "";
  };

  const formatTooltipTime = (minuteOfWeek) => {
    const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

    const dayIndex = Math.floor(minuteOfWeek / 1440);
    const minuteInDay = minuteOfWeek % 1440;

    const hour = Math.floor(minuteInDay / 60);
    const minute = minuteInDay % 60;

    return `${days[dayIndex] ?? ""} ${String(hour).padStart(2, "0")}:${String(
      minute
    ).padStart(2, "0")}`;
  };

  if (!dataset?.weeks?.length) {
    return <div style={{ padding: "1rem" }}>No data</div>;
  }

  

  return (
    <StyledChart ref={ref}>
      <VictoryChart
        scale={{ x: "linear" }}
        width={chartWidth}
        height={chartHeight}
        domain={{
          x: [0, 10080],
          y: [0, isPercentMode ? 100 : yMax],
        }}
        domainPadding={{ x: 0, y: 10 }}
        containerComponent={
          <VictoryZoomVoronoiContainer
            zoomDimension="x"
            allowZoom={allowZoom}
            allowPan={allowZoom}
            mouseFollowTooltips
            labels={({ datum }) => {
              const timeLabel = new Date(datum.serverTime).toLocaleString([], {
                day: "2-digit",
                month: "short",
                hour: "2-digit",
                minute: "2-digit",
                hour12: false,
              });

              const rawValue = Number(datum.rawValue ?? datum.y ?? 0);
              const capacityValue = Number(datum.capacityValue ?? 0);
              const percentValue = Number(datum.percentValue ?? 0);

              const primaryValue = isPercentMode
                ? `${percentValue.toFixed(1)}%`
                : `${rawValue}`;

              const secondaryValue = isPercentMode
                ? `${rawValue} / ${capacityValue}`
                : `${percentValue.toFixed(1)}%`;

              return `
                ${timeLabel}
                Occupied: ${primaryValue}
                Capacity: ${capacityValue}
                ${isPercentMode ? "Raw" : "Fill"}: ${secondaryValue}`;
            }}
            labelComponent={
              <VictoryTooltip
                flyoutStyle={{
                  fill: "rgba(10, 15, 25, 0.95)",
                  stroke: "#666",
                  strokeWidth: 1,
                }}
                style={{
                  fill: "#eee",
                  fontSize: 10,
                }}
                cornerRadius={6}
                pointerLength={6}
              />
            }
          />
        }
      >
        <VictoryAxis
          tickValues={xTickValues}
          tickFormat={formatMinuteOfWeek}
          style={{
            tickLabels: { fill: "#ccc", fontSize: 10 },
            axis: { stroke: "#ccc" },
            grid: { stroke: "#444", strokeDasharray: "4" },
          }}
        />

        <VictoryAxis
          dependentAxis
          tickValues={yTicks}
          tickFormat={(t) => (isPercentMode ? `${t}%` : t)}
          style={{
            tickLabels: { fill: "#ccc", fontSize: 10 },
            axis: { stroke: "#ccc" },
            grid: { stroke: "#444", strokeDasharray: "4" },
          }}
        />

        {weeks.map((week) => (
          <VictoryLine
            key={week.weekSequence}
            name={`week-${week.weekSequence}`}
            data={week.data}
            interpolation="monotoneX"
            style={{
              data: {
                stroke: week.color,
                strokeWidth: week.strokeWidth,
                opacity: week.opacity,
              },
            }}
          />
        ))}

        <VictoryLegend
          x={80}
          y={0}
          orientation="horizontal"
          gutter={20}
          style={{ labels: { fill: "#ccc", fontSize: 10 } }}
          data={[
            {
              name: "Current week strongest · older weeks fade",
              symbol: { fill: "var(--crane-height-md)" },
            },
          ]}
        />
      </VictoryChart>
    </StyledChart>
  );
}

export default WeeklySpectrum;
